import { CancelReason } from './cancelReason';
import { CarLicenseEnum } from './carLicense.enum';
import { RequiredEmploymentFields } from './customer';
import { EmploymentInvitee } from './employmentInvitee';
import { EmploymentTransfer } from './employmentTransferConfig';
import { LanguageEnum } from './language.enum';
import { MongoDocument } from './mongoDocument';
import { OrganisationAddress } from './organisation';
import { SexEnum } from './sex.enum';
import { MappedCertificate, MappedSpeciality } from './user';

export enum EmploymentStatusEnum {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  ARCHIVED = 'ARCHIVED',
  CANCELED = 'CANCELED',
}

export interface Employment extends MongoDocument {
  status: EmploymentStatusEnum;
  createdAt: Date;
  updatedAt: Date;
  customer: { id: string; displayName: string };
  department: { id: string; name: string; phone?: string; costCenter?: string };
  organisation: {
    id: string;
    name: string;
    customerName?: string;
    address?: OrganisationAddress;
  };
  editor: {
    userId: string;
    firstName: string;
    lastName: string;
  };
  createdBy: {
    userId: string;
    firstName: string;
    lastName: string;
  };
  lastUpdatedBy: {
    userId: string;
    firstName: string;
    lastName: string;
    customerId: string;
    customerName: string;
  };
  // yyyy-mm-dd
  startDate: string;
  // yyyy-mm-dd
  endDate: string;
  workload: number;
  // yyyy-mm-dd
  initialStartDate?: string;
  // yyyy-mm-dd
  initialEndDate?: string;
  initialWorkload?: number;
  description?: string;
  requiredUsers: number;
  ownerCustomerIds?: string[];
  car?: CarLicenseEnum;
  language?: LanguageEnum;
  certificates: MappedCertificate[];
  specialities: MappedSpeciality[];
  sex?: SexEnum;
  cancelReason?: CancelReason;
  cancelReasonDescription?: string;
  canceledBy?: string; //userId
  canceledAt?: Date;
  invitees: EmploymentInvitee[];
  transfers: EmploymentTransfer[];
  isDemo?: boolean;
  isGenerated?: boolean;
}

// update things only for a single transfer target customer
export type UpdateEmploymentTransferArgs = {
  employmentId: string;
  car?: CarLicenseEnum;
  language?: LanguageEnum;
  certificates?: MappedCertificate[];
  specialities?: MappedSpeciality[];
  sex?: SexEnum;
  editorId?: string;
};
export type UpdateEmploymentArgs = {
  _id: string;
  startDate?: string;
  endDate?: string;
  workload?: number;
  description?: string;
  car?: CarLicenseEnum;
  language?: LanguageEnum;
  certificates?: MappedCertificate[];
  specialities?: MappedSpeciality[];
  sex?: SexEnum;
  editor?: Employment['editor'];
  creatorId?: string;
  requiredUsers?: number;
  transfers?: EmploymentTransfer[];
};

export type CreateEmploymentArgs =
  // any update property except ID
  Omit<UpdateEmploymentArgs, '_id'> &
    // Picked Required properties for creation
    Pick<
      Employment,
      | 'startDate'
      | 'endDate'
      | 'workload'
      | 'editor'
      | 'department'
      | 'organisation'
      | 'requiredUsers'
    >;

export type CancelEmploymentArgs = {
  employmentId: string;
  reason: CancelReason;
  reasonDescription: string;
  informInviteeOfReason: boolean;
};

type DepartmentWithDisplayName = Employment['department'] & {
  displayName: string;
};

export interface EmploymentForNotification
  extends Omit<Employment, 'department'> {
  employmentColor?: 'red' | 'green' | 'gold';
  department: DepartmentWithDisplayName;
}

export type EmploymentExternalTransferInfo = {
  departmentDisplayName: string;
  startDate: string;
  endDate: string;
  workload: number;
  requiredUsers: number;
  status: EmploymentStatusEnum;
  invitees: EmploymentInvitee[];
  costCenter?: string;
  description?: string;
  declined?: boolean;
  sex?: SexEnum;
  certificates?: MappedCertificate[];
  specialities?: MappedSpeciality[];
  language?: LanguageEnum;
  car?: CarLicenseEnum;
  enableFiles?: boolean;
  ownTransfer?: EmploymentTransfer;
  requiredFields?: RequiredEmploymentFields;
  preventDirectAccept: boolean;
};
